'use client';

import { Vatan } from '@repo/icons';
import { Skeleton } from '@repo/ui/skeleton';
import { cn } from '@repo/utils/cn';
import { useQuery } from '@tanstack/react-query';
import { memo, useMemo } from 'react';
import { useAppStore } from '@/providers/AppStoreProvider';
import { useTenant } from '@/providers/TenantProvider';
import { getExchangeRate } from '@/server-actions/exchange-rate';

type VatanCoinPriceProps = {
  className?: string;
};

const VatanCoinPrice = ({ className }: VatanCoinPriceProps) => {
  const currency = useAppStore((state) => state.currency);

  const {
    tenantConfig: { settings },
  } = useTenant();

  const targetCurrency = useMemo(() => currency || 'USD', [currency]);

  const { data: exchangeRate, isLoading } = useQuery({
    queryKey: ['exchange-rate', 'VATAN', targetCurrency],
    queryFn: () => getExchangeRate({ srcCurrency: 'VATAN', dstCurrency: targetCurrency }),
    staleTime: 1000 * 60 * 60, // 1 hour
    refetchOnWindowFocus: true,
    enabled: settings.enableVatanCoinPromotion,
  });

  // Memoize the formatted price after exchangeRate is available
  const formattedPrice = useMemo(() => {
    return exchangeRate?.rate ? `$${Math.floor(Number(exchangeRate.rate) * 10000) / 10000}` : null;
  }, [exchangeRate?.rate]);

  if (!settings.enableVatanCoinPromotion || !exchangeRate) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex w-full animate-fade-in items-center justify-center space-x-2.5 rounded border p-1 vt-input-default-stroke-normal vt-containers-sidebar-bg rtl:space-x-reverse',
        className
      )}
    >
      <div className={cn('flex items-center justify-center rounded bg-[var(--colors-dark-600)] p-1.5')}>
        <Vatan />
      </div>
      {isLoading ? (
        <Skeleton className="h-9 w-full" />
      ) : (
        <>
          <div className="flex w-full flex-col items-start justify-center text-[#E2E3E4] vt-font-highlight-font-family vt-text-size-small">
            <span className="font-semibold">Vatan Coin</span>
            {formattedPrice && <span className="font-semibold">{formattedPrice}</span>}
          </div>
          <div className="flex items-center justify-center">
            <span
              dir="ltr"
              className="font-bold text-[#13B81E] vt-font-highlight-font-family vt-text-size-small-sm"
            >
              +2.43%
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(VatanCoinPrice);
